import React, { useEffect, useRef, useState } from 'react'
import { customer_attributes } from '../../services/dwhservices';
import { buyerKPIsColorCoding } from './ColorCoding';

// Module-level: survives component unmount/remount caused by tab switches.
// Keyed by glid so a new search always fetches fresh data.
const _buyerEngagementCache = new Map();
const _buyerEngagementInFlight = new Map(); // deduplicates concurrent fetches for same glid

const BuyerEngagement = React.memo(({ glid,empid, ak, setTopProductsList }) => {

    const [buyerData, setBuyerData] = useState([
        { id: 1, leftTitle: "BS Conflict Tickets", leftValue: "0/0", rightTitle: "Buy Lead 30 D", rightValue: 0 },
        { id: 2, leftTitle: "Enquiry Sent", leftValue: 0, rightTitle: "Calls Made", rightValue: 0 },
    ]);

    const fetchedGlid = useRef(null); // ← in-flight guard for the current mount

    const buyerTopMCATsList = (topMcats) => {
        const objectsArray = Object.values(topMcats);
        const nameCounts = objectsArray.reduce((acc, obj) => {
            const name = obj.glcat_mcat_name;
            if (name) {
                acc[name] = (acc[name] || 0) + 1;
            }
            return acc;
        }, {});
        const multipleNamesString = Object.keys(nameCounts)
            .filter(name => nameCounts[name] > 0)
            .join(', ');
        setTopProductsList(multipleNamesString);
    };

    const buyerEngagementKPIs = async (glid) => {
        if (_buyerEngagementInFlight.has(glid)) {
            const cached = await _buyerEngagementInFlight.get(glid);
            if (cached) {
                setBuyerData(cached.buyerData);
                setTopProductsList(cached.topProductsList);
            }
            return;
        }

        const promise = customer_attributes(glid, empid, ak, 1)
            .then(res => {
                if (!res) return null;
                res = res.data;
                if (res.status_code !== 200) return null;
                res = JSON.parse(res.response);
                if (!res.cust_attr.length) return null;
                const data = res.cust_attr[0];
                const buyerEngamentData = [
                    { id: 1, leftTitle: "BS Conflict Tickets", leftValue: `${data?.wip_tickets || 0}/${data?.total_tickets || 0}`, rightTitle: "Buy Lead 30 D", rightValue: `${data?.total_bl || 0}` },
                    { id: 2, leftTitle: "Enquiry Sent", leftValue: `${data?.enq_count || 0}`, rightTitle: "Calls Made", rightValue: `${data?.calls_count || 0}` },
                ];
                let topProductsList = '';
                if (data?.top_mcats) {
                    const topMCats = JSON.parse(data?.top_mcats);
                    topProductsList = Object.keys(
                        Object.values(topMCats).reduce((acc, obj) => {
                            const name = obj.glcat_mcat_name;
                            if (name) acc[name] = (acc[name] || 0) + 1;
                            return acc;
                        }, {})
                    ).join(', ');
                    buyerTopMCATsList(topMCats);
                }
                const result = { buyerData: buyerEngamentData, topProductsList };
                _buyerEngagementCache.set(glid, result);
                return result;
            })
            .catch(error => { console.error('Error:', error); return null; })
            .finally(() => _buyerEngagementInFlight.delete(glid));

        _buyerEngagementInFlight.set(glid, promise);
        const result = await promise;
        if (result) setBuyerData(result.buyerData);
    };

    // On remount (tab switch back): restore from module-level cache — no API call.
    // On new glid: cache miss → fetch and populate cache.
    // fetchedGlid ref guards against concurrent in-flight calls on the same mount.
    useEffect(() => {
        if (!glid) return;
        if (_buyerEngagementCache.has(glid)) {
            const { buyerData: cachedData, topProductsList } = _buyerEngagementCache.get(glid);
            setBuyerData(cachedData);
            setTopProductsList(topProductsList);
            return;
        }
        if (fetchedGlid.current === glid) return;
        fetchedGlid.current = glid;
        buyerEngagementKPIs(glid);
    }, [glid]);

    const Item = ({ leftTitle, leftValue, rightTitle, rightValue }) => {
        return (
            <div className="bg-bottom d-flex align-items-center">
                <div className="col-6">
                    <div className="d-flex align-items-center" style={{ height: '43px' }}>
                        <div className="col-8">
                            <h3>{leftTitle}</h3>
                        </div>
                        <div className="col-4">
                            <span style={{ 'color': `${buyerKPIsColorCoding(leftTitle, leftValue)}` }}>{leftValue}</span>
                        </div>
                    </div>
                </div>
                <div className="col-6 ps-3 bg-left">
                    <div className="d-flex align-items-center" style={{ height: '43px' }}>
                        <div className="col-9">
                            <h3>{rightTitle}</h3>
                        </div>
                        <div className="col-3">
                            <span>{rightValue}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="col-4 h-100 ps-3">
                <div className="table-box">
                    {buyerData.map(item => (
                        <Item
                            key={item.id}
                            leftTitle={item.leftTitle}
                            leftValue={item.leftValue}
                            rightTitle={item.rightTitle}
                            rightValue={item.rightValue}
                        />
                    ))}
                </div>
            </div>
        </>
    );
},
// Only re-render when glid changes — tab switches that keep glid the same are skipped.
(prevProps, nextProps) => prevProps.glid === nextProps.glid
);

export default BuyerEngagement;